import React from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  styled,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Checkbox,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { colors } from "../../utilities/src/Colors";

import StoreCreateController, {
  S,
  Props,
} from "./StoreCreateController.web";

const configJSON = require("./StoreLocatorConfig");

const dayLabels: Record<number, string> = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
};

export default class StoreCreate extends StoreCreateController {
  constructor(props: Props) {
    super(props);
    this.state = {
      form: this.getInitialForm(),
      imagePreviews: [],
      isSubmitting: false,
      errors: {},
    } as S;
  }

  render() {
    const { form, errors, isSubmitting, imagePreviews } = this.state;

    return (
      <MainWrapper>
        <Box className="pageWrapper" style={{ paddingTop: "10px" }}>
          <div style={webStyle.headerWrapper}>
            <PageTitle>Add Store</PageTitle>
          </div>

          <Paper style={webStyle.formPaper}>
            <Grid container spacing={3}>
              {/* Name EN */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="nameEnInput"
                  label="Name (English) *"
                  variant="outlined"
                  fullWidth
                  value={form.name_en}
                  onChange={(e) =>
                    this.handleFormChange("name_en", e.target.value)
                  }
                  error={!!errors.name_en}
                  helperText={errors.name_en}
                />
              </Grid>

              {/* Name AR */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="nameArInput"
                  label="Name (Arabic) *"
                  variant="outlined"
                  fullWidth
                  value={form.name_ar}
                  onChange={(e) =>
                    this.handleFormChange("name_ar", e.target.value)
                  }
                  error={!!errors.name_ar}
                  helperText={errors.name_ar}
                />
              </Grid>

              {/* Address EN */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="addressEnInput"
                  label="Address (English) *"
                  variant="outlined"
                  fullWidth
                  value={form.address_en}
                  onChange={(e) =>
                    this.handleFormChange("address_en", e.target.value)
                  }
                  error={!!errors.address_en}
                  helperText={errors.address_en}
                />
              </Grid>

              {/* Address AR */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="addressArInput"
                  label="Address (Arabic) *"
                  variant="outlined"
                  fullWidth
                  value={form.address_ar}
                  onChange={(e) =>
                    this.handleFormChange("address_ar", e.target.value)
                  }
                  error={!!errors.address_ar}
                  helperText={errors.address_ar}
                />
              </Grid>

              {/* Phone */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="phoneInput"
                  label="Phone"
                  variant="outlined"
                  fullWidth
                  value={form.phone}
                  onChange={(e) =>
                    this.handleFormChange("phone", e.target.value)
                  }
                />
              </Grid>

              {/* Position */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="positionInput"
                  label="Position"
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={form.position}
                  onChange={(e) =>
                    this.handleFormChange("position", e.target.value)
                  }
                />
              </Grid>

              {/* Latitude */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="latitudeInput"
                  label="Latitude"
                  variant="outlined"
                  fullWidth
                  value={form.latitude}
                  onChange={(e) =>
                    this.handleFormChange("latitude", e.target.value)
                  }
                />
              </Grid>

              {/* Longitude */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="longitudeInput"
                  label="Longitude"
                  variant="outlined"
                  fullWidth
                  value={form.longitude}
                  onChange={(e) =>
                    this.handleFormChange("longitude", e.target.value)
                  }
                />
              </Grid>

              {/* Google Place ID */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="googlePlaceIdInput"
                  label="Google Place ID"
                  variant="outlined"
                  fullWidth
                  value={form.google_place_id}
                  onChange={(e) =>
                    this.handleFormChange("google_place_id", e.target.value)
                  }
                />
              </Grid>

              {/* Google Maps URL */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="googleMapsUrlInput"
                  label="Google Maps URL"
                  variant="outlined"
                  fullWidth
                  value={form.google_maps_url}
                  onChange={(e) =>
                    this.handleFormChange("google_maps_url", e.target.value)
                  }
                />
              </Grid>

              {/* Rating */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="ratingInput"
                  label="Rating"
                  variant="outlined"
                  fullWidth
                  value={form.rating}
                  onChange={(e) =>
                    this.handleFormChange("rating", e.target.value)
                  }
                />
              </Grid>

              {/* Total Ratings */}
              <Grid item xs={12} sm={6}>
                <TextField
                  data-test-id="totalRatingsInput"
                  label="Total Ratings"
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={form.total_ratings}
                  onChange={(e) =>
                    this.handleFormChange("total_ratings", e.target.value)
                  }
                />
              </Grid>

              {/* Is Active */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      data-test-id="isActiveSwitch"
                      checked={form.is_active}
                      onChange={(e) =>
                        this.handleFormChange("is_active", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>

              {/* Images */}
              <Grid item xs={12}>
                <ImageUploadBox>
                  <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600 }}>
                    Store Images
                  </Typography>
                  <input
                    data-test-id="imagesInput"
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    multiple
                    onChange={this.handleImagesChange}
                    style={webStyle.fileInput}
                  />
                  <div style={webStyle.previewRow}>
                    {imagePreviews.map((preview, idx) => (
                      <div key={idx} style={webStyle.previewItem}>
                        <img
                          src={preview}
                          alt={`preview-${idx}`}
                          style={webStyle.imagePreview}
                        />
                        <IconButton
                          size="small"
                          style={webStyle.removeButton}
                          onClick={() => this.handleRemoveImage(idx)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                </ImageUploadBox>
              </Grid>

              {/* Store Times */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
                  Store Hours
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <StyledHeaderCell>Day</StyledHeaderCell>
                      <StyledHeaderCell>Opening Time</StyledHeaderCell>
                      <StyledHeaderCell>Closing Time</StyledHeaderCell>
                      <StyledHeaderCell>Closed</StyledHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {form.store_times.map((time, index) => (
                      <TableRow key={time.day}>
                        <TableCell>{dayLabels[time.day]}</TableCell>
                        <TableCell>
                          <TextField
                            data-test-id={`openingTime-${index}`}
                            type="time"
                            size="small"
                            variant="outlined"
                            value={time.opening_time}
                            disabled={time.is_closed}
                            onChange={(e) =>
                              this.handleStoreTimeChange(
                                index,
                                "opening_time",
                                e.target.value
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            data-test-id={`closingTime-${index}`}
                            type="time"
                            size="small"
                            variant="outlined"
                            value={time.closing_time}
                            disabled={time.is_closed}
                            onChange={(e) =>
                              this.handleStoreTimeChange(
                                index,
                                "closing_time",
                                e.target.value
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            data-test-id={`isClosed-${index}`}
                            checked={time.is_closed}
                            onChange={(e) =>
                              this.handleStoreTimeChange(
                                index,
                                "is_closed",
                                e.target.checked
                              )
                            }
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>

              {/* Buttons */}
              <Grid item xs={12}>
                <div style={webStyle.buttonRow}>
                  <Button
                    data-test-id="cancelButton"
                    variant="outlined"
                    onClick={this.handleCancel}
                    style={webStyle.cancelButton}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-test-id="submitButton"
                    variant="contained"
                    onClick={this.handleSubmit}
                    disabled={isSubmitting}
                    style={webStyle.submitButton}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} style={{ color: "#fff" }} />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </MainWrapper>
    );
  }
}

// Customizable Area Start
const PageTitle = styled("div")({
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: "24px",
  lineHeight: "29px",
  "@media only screen and (max-width: 1024px)": {
    fontSize: "16px",
  },
});

const MainWrapper = styled("div")({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
  },
});

const ImageUploadBox = styled("div")({
  border: "1px dashed #ccc",
  borderRadius: "8px",
  padding: "16px",
});

const StyledHeaderCell = styled(TableCell)({
  fontWeight: 600,
  fontFamily: "Montserrat",
  backgroundColor: "#f5f5f5",
});

const webStyle = {
  headerWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 32,
    alignItems: "center",
  },
  formPaper: {
    padding: 32,
    borderRadius: 8,
  },
  fileInput: {
    marginTop: 8,
  },
  previewRow: {
    display: "flex",
    flexWrap: "wrap" as "wrap",
    gap: 12,
    marginTop: 12,
  },
  previewItem: {
    position: "relative" as "relative",
    display: "inline-block",
  },
  imagePreview: {
    width: 100,
    height: 80,
    objectFit: "cover" as "cover",
    borderRadius: 4,
  },
  removeButton: {
    position: "absolute" as "absolute",
    top: -8,
    right: -8,
    background: "#fff",
    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
  },
  buttonRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 16,
    marginTop: 16,
  },
  cancelButton: {
    borderColor: colors().cyancobaltblue,
    color: colors().cyancobaltblue,
    height: 56,
    width: 160,
    borderRadius: 8,
    textTransform: "none" as "none",
    fontWeight: 600,
    fontSize: 16,
  },
  submitButton: {
    background: colors().cyancobaltblue,
    color: "#fff",
    height: 56,
    width: 160,
    borderRadius: 8,
    textTransform: "none" as "none",
    fontWeight: 600,
    fontSize: 16,
  },
};
// Customizable Area End
